/**
 * useTodoState - Central state management hook for the To-Do feature.
 * Fetches tasks on mount, provides optimistic add/toggle/delete/reorder.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Task } from '@/types/tasks';
import type { TodoSortOption, TodoFilterOption } from '@/types/todo-preferences';
import { TasksApiClient } from '@/lib/client/TasksApiClient';
import { TodoCache } from '@/lib/client/TodoCache';
import { useSupabaseAuth } from '@/lib/supabase';

type TodoState = {
  activeTasks: Task[];
  completedTasks: Task[];
  loading: boolean;
  refreshing: boolean;
  addTask: (title: string, priority?: 'high' | null) => Promise<void>;
  toggleTask: (taskId: string) => Promise<void>;
  togglePriority: (taskId: string) => Promise<void>;
  deleteTask: (taskId: string) => Promise<void>;
  reorderTaskById: (taskId: string, toIndex: number) => void;
  updateTaskTitle: (taskId: string, newTitle: string) => Promise<void>;
  updateTaskColor: (taskId: string, color: string) => Promise<void>;
  toggleSubtask: (taskId: string, subtaskId: string) => Promise<void>;
  addSubtask: (taskId: string, title: string, options?: { afterSubtaskId?: string }) => Promise<string | null>;
  updateSubtaskTitle: (taskId: string, subtaskId: string, newTitle: string) => Promise<void>;
  deleteSubtask: (taskId: string, subtaskId: string) => Promise<void>;
  refresh: () => Promise<void>;
};

const REORDER_DEBOUNCE_MS = 500;

/**
 * Strips placeholder subtasks (blank-title rows from chain submits) from a
 * persist payload. Blank rows live in local state only — the API rejects them
 * with a Zod min-length validation error.
 */
function persistableSubtasks(
  subtasks: Task['subtasks'],
): Task['subtasks'] {
  if (!subtasks) return subtasks;
  return subtasks.filter(s => s.title.trim().length > 0);
}

type UseTodoStateOptions = {
  sort?: TodoSortOption;
  filter?: TodoFilterOption;
  listId?: string | null;
  /** When true, the network fetch on mount/listId-change is skipped. Hydration
   *  from TodoCache still happens. When this prop flips back to false, one
   *  fresh refresh fires to reconcile with the server. Used to avoid a flash
   *  of stale data while an optimistic cross-list operation is in flight. */
  suppressInitialFetch?: boolean;
};

export function useTodoState(options: UseTodoStateOptions = {}): TodoState {
  const { sort = 'manual', filter = 'all', listId = null, suppressInitialFetch = false } = options;
  const { session } = useSupabaseAuth();
  const userId = session?.user?.id ?? null;
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const reorderTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const client = useMemo(() => {
    if (!session?.access_token) return null;
    return new TasksApiClient(session.access_token);
  }, [session?.access_token]);

  const fetchTasks = useCallback(async () => {
    if (!client || !listId || !userId) return;
    try {
      const fetched = await client.listTasks(listId);
      setTasks(fetched);
      TodoCache.setTasks(userId, listId, fetched);
    } catch (error) {
      console.error('[useTodoState] Failed to fetch tasks:', error);
    }
  }, [client, listId, userId]);

  // Clear stale tasks when switching lists so we don't flash the previous
  // list's data while the new list hydrates.
  const prevListIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevListIdRef.current !== null && prevListIdRef.current !== listId) {
      setTasks([]);
    }
    prevListIdRef.current = listId;
  }, [listId]);

  // Hydrate from cache + fetch fresh in parallel. Cache read is ~10-50ms so
  // subsequent tab mounts render instantly while the network request reconciles.
  // When `suppressInitialFetch` is true (e.g. an optimistic cross-list move is
  // mid-flight), we skip the network fetch and rely solely on the cache so the
  // optimistic state isn't briefly overwritten by stale server data.
  useEffect(() => {
    if (!client || !listId || !userId) return;
    let mounted = true;

    TodoCache.getTasks(userId, listId).then(cached => {
      if (!mounted || !cached) return;
      // Only hydrate if the network fetch hasn't already populated tasks.
      setTasks(prev => (prev.length > 0 ? prev : cached));
      setLoading(false);
    });

    if (suppressInitialFetch) {
      setLoading(false);
      return () => { mounted = false; };
    }

    (async () => {
      try {
        const fetched = await client.listTasks(listId);
        if (mounted) {
          setTasks(fetched);
          TodoCache.setTasks(userId, listId, fetched);
        }
      } catch (error) {
        console.error('[useTodoState] Initial load failed:', error);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => { mounted = false; };
  }, [client, userId, listId, suppressInitialFetch]);

  // When `suppressInitialFetch` flips from true → false (the optimistic op
  // finished), reconcile with the server.
  const prevSuppressRef = useRef(suppressInitialFetch);
  useEffect(() => {
    const wasSuppressed = prevSuppressRef.current;
    prevSuppressRef.current = suppressInitialFetch;
    if (wasSuppressed && !suppressInitialFetch) {
      void fetchTasks();
    }
  }, [suppressInitialFetch, fetchTasks]);

  // Persist mutations to the cache so the next cold open sees the latest state.
  useEffect(() => {
    if (!userId || !listId || loading) return;
    const timer = setTimeout(() => {
      TodoCache.setTasks(userId, listId, tasks);
    }, 300);
    return () => clearTimeout(timer);
  }, [tasks, userId, listId, loading]);

  // Clear the loading state for signed-out users so the empty state can render.
  useEffect(() => {
    if (!client) setLoading(false);
  }, [client]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    await fetchTasks();
    setRefreshing(false);
  }, [fetchTasks]);

  const activeTasks = useMemo(() => {
    // Apply filter
    let filtered = tasks.filter(t => !t.completed);
    if (filter === 'priority-only') {
      filtered = filtered.filter(t => t.priority === 'high');
    } else if (filter === 'normal-only') {
      filtered = filtered.filter(t => t.priority !== 'high');
    }

    // Apply sort
    return filtered.sort((a, b) => {
      switch (sort) {
        case 'alpha-asc':
          return a.title.localeCompare(b.title);
        case 'alpha-desc':
          return b.title.localeCompare(a.title);
        case 'newest':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'oldest':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case 'priority-first': {
          const aPriority = a.priority === 'high' ? 0 : 1;
          const bPriority = b.priority === 'high' ? 0 : 1;
          if (aPriority !== bPriority) return aPriority - bPriority;
          return a.orderIndex - b.orderIndex;
        }
        case 'normal-first': {
          const aPriority = a.priority === 'high' ? 1 : 0;
          const bPriority = b.priority === 'high' ? 1 : 0;
          if (aPriority !== bPriority) return aPriority - bPriority;
          return a.orderIndex - b.orderIndex;
        }
        case 'manual':
        default:
          return a.orderIndex - b.orderIndex;
      }
    });
  }, [tasks, sort, filter]);

  const completedTasks = useMemo(() => {
    const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000;
    return tasks
      .filter(t => t.completed && new Date(t.updatedAt).getTime() > twentyFourHoursAgo)
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }, [tasks]);

  const addTask = useCallback(async (title: string, priority?: 'high' | null) => {
    if (!client || !listId) return;

    const tempId = `temp-${Date.now()}`;
    const now = new Date().toISOString();

    // Use functional update to calculate maxOrder from current state
    setTasks(prev => {
      const maxOrder = prev.reduce((max, t) => Math.max(max, t.orderIndex), -1);
      const optimisticTask: Task = {
        id: tempId,
        userId: session?.user?.id ?? '',
        listId,
        title,
        completed: false,
        priority: priority ?? undefined,
        orderIndex: maxOrder + 1,
        createdAt: now,
        updatedAt: now,
      };
      return [...prev, optimisticTask];
    });

    try {
      const input: Parameters<typeof client.createTask>[0] = priority
        ? { title, priority, listId }
        : { title, listId };
      const created = await client.createTask(input);
      setTasks(prev => prev.map(t => (t.id === tempId ? created : t)));
    } catch (error) {
      console.error('[useTodoState] Failed to create task:', error);
      setTasks(prev => prev.filter(t => t.id !== tempId));
    }
  }, [client, listId, session?.user?.id]);

  const toggleTask = useCallback(async (taskId: string) => {
    if (!client) return;
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    const newCompleted = !task.completed;
    setTasks(prev =>
      prev.map(t =>
        t.id === taskId
          ? { ...t, completed: newCompleted, updatedAt: new Date().toISOString() }
          : t,
      ),
    );

    try {
      await client.updateTask(taskId, { completed: newCompleted });
    } catch (error) {
      console.error('[useTodoState] Failed to toggle task:', error);
      setTasks(prev =>
        prev.map(t => (t.id === taskId ? { ...t, completed: !newCompleted } : t)),
      );
    }
  }, [client, tasks]);

  const togglePriority = useCallback(async (taskId: string) => {
    if (!client) return;
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    const newPriority = task.priority === 'high' ? null : 'high' as const;
    setTasks(prev =>
      prev.map(t =>
        t.id === taskId ? { ...t, priority: newPriority } : t,
      ),
    );

    try {
      await client.updateTask(taskId, { priority: newPriority });
    } catch (error) {
      console.error('[useTodoState] Failed to toggle priority:', error);
      setTasks(prev =>
        prev.map(t => (t.id === taskId ? { ...t, priority: task.priority } : t)),
      );
    }
  }, [client, tasks]);

  const deleteTask = useCallback(async (taskId: string) => {
    if (!client) return;
    const previous = tasks;
    setTasks(prev => prev.filter(t => t.id !== taskId));

    try {
      await client.deleteTask(taskId);
    } catch (error) {
      console.error('[useTodoState] Failed to delete task:', error);
      setTasks(previous);
    }
  }, [client, tasks]);

  const reorderTaskById = useCallback((taskId: string, toIndex: number) => {
    if (!client) return;

    setTasks(prev => {
      const active = prev.filter(t => !t.completed).sort((a, b) => a.orderIndex - b.orderIndex);
      const completed = prev.filter(t => t.completed);

      const fromIndex = active.findIndex(t => t.id === taskId);
      if (fromIndex < 0 || fromIndex === toIndex) return prev;

      const [moved] = active.splice(fromIndex, 1);
      active.splice(toIndex, 0, moved);

      const reindexed = active.map((t, i) => ({ ...t, orderIndex: i }));
      const serverTaskIds = reindexed
        .filter(t => !t.id.startsWith('temp-'))
        .map(t => t.id);

      // Debounced API call — update each task's orderIndex individually
      if (reorderTimerRef.current) clearTimeout(reorderTimerRef.current);
      const tasksToUpdate = reindexed.filter(t => !t.id.startsWith('temp-'));
      if (tasksToUpdate.length > 0) {
        reorderTimerRef.current = setTimeout(() => {
          Promise.all(
            tasksToUpdate.map(t =>
              client.updateTask(t.id, { orderIndex: t.orderIndex }),
            ),
          ).catch(error => {
            console.error('[useTodoState] Failed to reorder tasks:', error);
          });
        }, REORDER_DEBOUNCE_MS);
      }

      return [...reindexed, ...completed];
    });
  }, [client]);

  const updateTaskTitle = useCallback(async (taskId: string, newTitle: string) => {
    if (!client) return;

    let previousTitle: string | null = null;
    let shouldUpdate = false;

    setTasks(prev => {
      const task = prev.find(t => t.id === taskId);
      if (!task || task.title === newTitle) return prev;
      previousTitle = task.title;
      shouldUpdate = true;
      return prev.map(t => t.id === taskId ? { ...t, title: newTitle } : t);
    });

    if (!shouldUpdate) return;

    try {
      await client.updateTask(taskId, { title: newTitle });
    } catch (error) {
      console.error('[useTodoState] Failed to update task title:', error);
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, title: previousTitle! } : t));
    }
  }, [client]);

  const updateTaskColor = useCallback(async (taskId: string, color: string) => {
    if (!client) return;

    let previousColor: string | null = null;
    let shouldUpdate = false;

    setTasks(prev => {
      const task = prev.find(t => t.id === taskId);
      if (!task || task.color === color) return prev;
      previousColor = task.color ?? null;
      shouldUpdate = true;
      return prev.map(t => t.id === taskId ? { ...t, color } : t);
    });

    if (!shouldUpdate) return;

    try {
      await client.updateTask(taskId, { color });
    } catch (error) {
      console.error('[useTodoState] Failed to update task color:', error);
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, color: previousColor ?? undefined } : t));
    }
  }, [client]);

  const toggleSubtask = useCallback(async (taskId: string, subtaskId: string) => {
    if (!client) return;

    let previousSubtasks: Task['subtasks'] = undefined;
    let updatedSubtasks: Task['subtasks'] = undefined;

    setTasks(prev => {
      const task = prev.find(t => t.id === taskId);
      if (!task?.subtasks) return prev;
      previousSubtasks = task.subtasks;
      updatedSubtasks = task.subtasks.map(s =>
        s.id === subtaskId ? { ...s, completed: !s.completed } : s,
      );
      return prev.map(t => t.id === taskId ? { ...t, subtasks: updatedSubtasks } : t);
    });

    if (!updatedSubtasks) return;

    try {
      await client.updateTask(taskId, { subtasks: persistableSubtasks(updatedSubtasks) });
    } catch (error) {
      console.error('[useTodoState] Failed to toggle subtask:', error);
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, subtasks: previousSubtasks } : t));
    }
  }, [client]);

  const addSubtask = useCallback(async (
    taskId: string,
    title: string,
    options?: { afterSubtaskId?: string },
  ): Promise<string | null> => {
    if (!client) return null;

    let previousSubtasks: Task['subtasks'] = undefined;
    let updatedSubtasks: Task['subtasks'] = undefined;
    const newSubtaskId = `sub-${Date.now()}`;

    setTasks(prev => {
      const task = prev.find(t => t.id === taskId);
      if (!task) return prev;
      previousSubtasks = task.subtasks;
      const newSubtask = { id: newSubtaskId, title, completed: false };
      const existing = task.subtasks ?? [];
      const afterId = options?.afterSubtaskId;
      const insertIdx = afterId ? existing.findIndex(s => s.id === afterId) : -1;
      updatedSubtasks = insertIdx >= 0
        ? [...existing.slice(0, insertIdx + 1), newSubtask, ...existing.slice(insertIdx + 1)]
        : [...existing, newSubtask];
      return prev.map(t => t.id === taskId ? { ...t, subtasks: updatedSubtasks } : t);
    });

    if (!updatedSubtasks) return null;

    // Skip the server persist for blank chain rows — the API rejects empty
    // subtask titles. The row lives in local state only until the user types,
    // at which point updateSubtaskTitle will persist the full array (including
    // this row, now with a title). Blank rows abandoned by the user are deleted
    // locally before any persist would have happened.
    if (title.trim().length === 0) {
      return newSubtaskId;
    }

    // Fire-and-forget persist with rollback so callers don't wait on the network
    void (async () => {
      try {
        await client.updateTask(taskId, { subtasks: persistableSubtasks(updatedSubtasks) });
      } catch (error) {
        console.error('[useTodoState] Failed to add subtask:', error);
        setTasks(prev => prev.map(t => t.id === taskId ? { ...t, subtasks: previousSubtasks } : t));
      }
    })();

    return newSubtaskId;
  }, [client]);

  const updateSubtaskTitle = useCallback(async (taskId: string, subtaskId: string, newTitle: string) => {
    if (!client) return;

    let previousSubtasks: Task['subtasks'] = undefined;
    let updatedSubtasks: Task['subtasks'] = undefined;

    setTasks(prev => {
      const task = prev.find(t => t.id === taskId);
      if (!task?.subtasks) return prev;
      const subtask = task.subtasks.find(s => s.id === subtaskId);
      if (!subtask || subtask.title === newTitle) return prev;
      previousSubtasks = task.subtasks;
      updatedSubtasks = task.subtasks.map(s =>
        s.id === subtaskId ? { ...s, title: newTitle } : s,
      );
      return prev.map(t => t.id === taskId ? { ...t, subtasks: updatedSubtasks } : t);
    });

    if (!updatedSubtasks) return;

    try {
      await client.updateTask(taskId, { subtasks: persistableSubtasks(updatedSubtasks) });
    } catch (error) {
      console.error('[useTodoState] Failed to update subtask title:', error);
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, subtasks: previousSubtasks } : t));
    }
  }, [client]);

  const deleteSubtask = useCallback(async (taskId: string, subtaskId: string) => {
    if (!client) return;

    let previousSubtasks: Task['subtasks'] = undefined;
    let updatedSubtasks: Task['subtasks'] = undefined;

    setTasks(prev => {
      const task = prev.find(t => t.id === taskId);
      if (!task?.subtasks) return prev;
      previousSubtasks = task.subtasks;
      updatedSubtasks = task.subtasks.filter(s => s.id !== subtaskId);
      return prev.map(t => t.id === taskId ? { ...t, subtasks: updatedSubtasks } : t);
    });

    if (!updatedSubtasks) return;

    try {
      await client.updateTask(taskId, { subtasks: persistableSubtasks(updatedSubtasks) });
    } catch (error) {
      console.error('[useTodoState] Failed to delete subtask:', error);
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, subtasks: previousSubtasks } : t));
    }
  }, [client]);

  return {
    activeTasks,
    completedTasks,
    loading,
    refreshing,
    addTask,
    toggleTask,
    togglePriority,
    deleteTask,
    reorderTaskById,
    updateTaskTitle,
    updateTaskColor,
    toggleSubtask,
    addSubtask,
    updateSubtaskTitle,
    deleteSubtask,
    refresh,
  };
}
